'use client';

import type { KeenSliderInstance } from 'keen-slider';
import { useKeenSlider } from 'keen-slider/react';
import Image from 'next/image';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import LinkButtonPrimary from '../_lib/ui/LinkButtonPrimary';
import LinkButtonSecondary from '../_lib/ui/LinkButtonSecondary';

import 'keen-slider/keen-slider.min.css';

// --- Interfaces ---
interface ImageType {
  id: number;
  src: string;
  caption: string;
  heading?: string;
  subheading?: string;
}

interface CarouselProps {
  images?: ImageType[];
  slides?: ImageType[];
  isGallery?: boolean;
  prioritizeFirstImage?: boolean;
  onSliderReady?: (slider: KeenSliderInstance) => void;
}

// Custom hook to detect prefers-reduced-motion
const usePrefersReducedMotion = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    // Check if window is available (client-side)
    if (typeof window !== 'undefined') {
      // Initial check
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setPrefersReducedMotion(mediaQuery.matches);

      // Add listener for changes
      const handleChange = (e: MediaQueryListEvent) => {
        setPrefersReducedMotion(e.matches);
      };

      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      }
    }
  }, []);

  return prefersReducedMotion;
};

// --- Carousel Component ---
const Carousel: React.FC<CarouselProps> = ({
  images,
  slides,
  isGallery,
  prioritizeFirstImage = true,
  onSliderReady,
}) => {
  const [isPaused, setIsPaused] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();

  // Configure autoplay options
  const autoplayDelay = prefersReducedMotion ? 8000 : 6000;

  // Setup keen-slider with autoplay
  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>(
    {
      loop: true,
      slides: { perView: 1 },
      drag: true,
      created(s) {
        setCurrentSlide(s.track.details.rel);
        onSliderReady?.(s);
      },
      slideChanged(s) {
        setCurrentSlide(s.track.details.rel);
      },
    },
    [
      // Autoplay plugin
      (slider) => {
        let timeout: ReturnType<typeof setTimeout>;
        let mouseOver = false;

        function clearNextTimeout() {
          clearTimeout(timeout);
        }

        function nextTimeout() {
          clearTimeout(timeout);
          if (mouseOver || isPaused) return;
          timeout = setTimeout(() => {
            slider.next();
          }, autoplayDelay);
        }

        slider.on('created', () => {
          slider.container.addEventListener('mouseover', () => {
            mouseOver = true;
            clearNextTimeout();
          });
          slider.container.addEventListener('mouseout', () => {
            mouseOver = false;
            nextTimeout();
          });
          nextTimeout();
        });

        slider.on('dragStarted', clearNextTimeout);
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);

        return () => {
          clearNextTimeout();
        };
      },
    ],
  );

  // Memoize type guards to avoid recreating them on each render
  const isImageType = useCallback((item: unknown): item is ImageType => {
    return (
      item !== null &&
      typeof item === 'object' &&
      'src' in item &&
      'caption' in item &&
      typeof (item as ImageType).src === 'string' &&
      typeof (item as ImageType).caption === 'string'
    );
  }, []);

  const isSlideType = useCallback((item: unknown): item is ImageType => {
    return (
      item !== null &&
      typeof item === 'object' &&
      'src' in item &&
      'caption' in item &&
      'heading' in item &&
      'subheading' in item &&
      typeof (item as ImageType).src === 'string' &&
      typeof (item as ImageType).caption === 'string' &&
      typeof (item as ImageType).heading === 'string' &&
      typeof (item as ImageType).subheading === 'string'
    );
  }, []);

  // Memoize items to prevent unnecessary re-renders
  const items = useMemo(() => (isGallery ? images || [] : slides || []), [isGallery, images, slides]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        instanceRef.current?.prev();
      } else if (e.key === 'ArrowRight') {
        instanceRef.current?.next();
      } else if (e.key === ' ' || e.key === 'Spacebar') {
        setIsPaused((prev) => !prev);
        e.preventDefault();
      }
    },
    [instanceRef],
  );

  // Add passive event listeners to improve scrolling performance
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown, { passive: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <section
      className={`relative overflow-hidden ${isGallery ? 'h-[60vh]' : 'h-[90vh]'}`}
      id={isGallery ? 'photo-slideshow' : 'hero-slideshow'}
      aria-label={isGallery ? 'Photo gallery slideshow' : 'Featured content slideshow'}
      aria-roledescription='carousel'
    >
      {/* Pause/Play Button */}
      <div className='absolute top-4 right-4 z-20'>
        <button
          type='button'
          onClick={() => setIsPaused(!isPaused)}
          className='rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white/40 focus:ring-2 focus:ring-white focus:outline-none dark:bg-gray-800/40 dark:hover:bg-gray-800/60'
          aria-label={isPaused ? 'Play slideshow' : 'Pause slideshow'}
        >
          {isPaused ? (
            <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z'
              />
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          ) : (
            <svg className='h-5 w-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z'
              />
            </svg>
          )}
        </button>
      </div>

      {/* Keen Slider Container */}
      <div ref={sliderRef} className='keen-slider flex h-full w-full flex-nowrap'>
        {items.map((item, index) => {
          const imageSrc = item.src || '';
          const imageAlt = item.caption || '';
          const isFirstSlide = index === 0;
          const isActive = currentSlide === index;

          return (
            <div
              className='keen-slider__slide relative h-full w-full flex-shrink-0'
              key={item.id}
              role='group'
              aria-roledescription='slide'
              aria-label={`Slide ${index + 1} of ${items.length}`}
            >
              <Image
                src={imageSrc}
                alt={imageAlt}
                quality={isFirstSlide ? 90 : 75} // Keep high quality only for first slide
                priority={isFirstSlide && prioritizeFirstImage} // Only prioritize first slide
                fill
                className='object-cover'
                sizes='(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 70vw'
                loading={isFirstSlide ? 'eager' : 'lazy'}
                decoding={isFirstSlide ? 'sync' : 'async'}
                placeholder='blur'
                blurDataURL={imageSrc.replace(/(\.[^.]+)$/, '-blur$1')}
              />
              <div className='absolute inset-0 bg-black/40' />
              <div className='absolute inset-0 z-10 flex flex-col items-center justify-center px-4 text-center'>
                <div
                  className={`mx-auto max-w-4xl transition-all duration-800 ease-out ${
                    isActive ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
                  }`}
                  style={{
                    transitionDelay: prefersReducedMotion ? '0s' : '0.2s',
                    transitionDuration: prefersReducedMotion ? '0.1s' : '0.8s',
                  }}
                >
                  {!isGallery && isSlideType(item) && (
                    <>
                      <h2 className='mb-4 px-6 text-4xl leading-8 font-bold text-white drop-shadow-lg transition-all duration-150 ease-in-out sm:px-10 md:px-0 md:text-5xl md:leading-14 lg:text-6xl'>
                        {item.heading}
                      </h2>
                      <p className='px-6 text-xl leading-5 text-white/90 drop-shadow-md transition-all duration-150 ease-in-out sm:px-10 md:px-0 md:text-2xl md:leading-6'>
                        {item.subheading}
                      </p>
                      <div className='mt-8 flex flex-col items-center justify-center gap-x-4 gap-y-3 md:flex-row'>
                        <LinkButtonPrimary href='#contact-us' aria-label='Contact us to learn more' className='group'>
                          Contact Us
                          <svg
                            className={`ml-2 h-5 w-5 ${prefersReducedMotion ? '' : 'transition-transform group-hover:translate-x-1'}`}
                            fill='none'
                            stroke='currentColor'
                            viewBox='0 0 24 24'
                            aria-hidden={true}
                          >
                            <path
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              strokeWidth={2}
                              d='M14 5l7 7m0 0l-7 7m7-7H3'
                            />
                          </svg>
                        </LinkButtonPrimary>
                        <LinkButtonSecondary href='#about' aria-label='Learn more about our services'>
                          Learn More
                        </LinkButtonSecondary>
                      </div>
                    </>
                  )}
                  {isGallery && isImageType(item) && (
                    <div className='rounded-lg bg-black/50 px-4 py-2 backdrop-blur-sm'>
                      <p className='text-lg text-white'>{item.caption}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Prev/Next Buttons */}
      <div className='absolute inset-y-0 left-4 z-10 flex items-center'>
        <button
          type='button'
          onClick={() => instanceRef.current?.prev()}
          className='rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white/40 focus:ring-2 focus:ring-white focus:outline-none dark:bg-gray-800/40 dark:hover:bg-gray-800/60'
          aria-label='Previous slide'
        >
          <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M15 19l-7-7 7-7' />
          </svg>
        </button>
      </div>
      <div className='absolute inset-y-0 right-4 z-10 flex items-center'>
        <button
          type='button'
          onClick={() => instanceRef.current?.next()}
          className='rounded-full bg-white/20 p-2 text-white backdrop-blur-sm transition-colors duration-300 hover:bg-white/40 focus:ring-2 focus:ring-white focus:outline-none'
          aria-label='Next slide'
        >
          <svg className='h-6 w-6' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 5l7 7-7 7' />
          </svg>
        </button>
      </div>

      {/* Dot Navigation */}
      {instanceRef.current && (
        <div className='absolute right-0 bottom-8 left-0 z-10 flex justify-center space-x-5'>
          {[...Array(items.length)].map((_, index) => (
            <button
              type='button'
              key={index}
              onClick={() => instanceRef.current?.moveToIdx(index)}
              aria-label={`Go to slide ${index + 1}`}
              className={`h-5 w-5 rounded-full transition-all duration-300 focus:ring-2 focus:ring-white focus:outline-none ${
                index === currentSlide ? 'scale-125 bg-white' : 'bg-white/40 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      )}

      {/* Bottom Gradient */}
      <div className='pointer-events-none absolute right-0 bottom-0 left-0 z-10 h-24 bg-gradient-to-t from-white to-transparent dark:from-gray-900' />

      {/* Marker div for header intersection observer */}
      <div id='carousel-bottom-marker' className='absolute bottom-0' />
    </section>
  );
};

export default Carousel;
